import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../../../shared/components/form';
import { countries } from '../../../shared/constants/ccBill';
import { MASTERCARD_ONLY_COUNTRIES, VISA_ONLY_COUNTRIES } from '../../../shared/constants/ccBill';
import { getStatesForCountry } from '../../../shared/utils/locationUtils';
import apiCache from '../../../shared/utils/apiCache';
import { reportError } from '../../../shared/sentry';

const BillingAddressForm = ({
  form,
  shouldShowError,
  isEditMode = false,
  initialCountry = null,
  initialState = null,
  accountData = null,
}) => {
  const [availableStates, setAvailableStates] = useState([]);
  const watchedValues = form.watch();
  const isInitialMount = useRef(true);
  const hasInitializedStates = useRef(false);

  // Utility to fetch states from API
  const fetchStatesFromAPI = useCallback(async (countryCode) => {
    try {
      const countryName = countries.find(c => c.code === countryCode)?.name || countryCode;
      return await apiCache.getStatesForCountry(countryName);
    } catch (err) {
      reportError(err, 'BillingAddressForm fetch states');
      return [];
    }
  }, []);

  // Handle country change
  const handleCountryChange = useCallback(async (countryCode) => {
    const upperCountry = countryCode.toUpperCase().slice(0, 2);
    const currentState = form.getValues('state');
    form.setValue('country', upperCountry);
    form.clearErrors('country');

    let states = await fetchStatesFromAPI(upperCountry);
    if (!states.length) {
      states = getStatesForCountry(upperCountry);
    }
    setAvailableStates(states);
    
    // Preserve state if it's still valid for the new country
    if (currentState && states.length > 0) {
      const stateObj = states.find(s =>
        s.name.toLowerCase() === currentState.toLowerCase() ||
        s.code.toLowerCase() === currentState.toLowerCase()
      );
      if (stateObj) {
        form.setValue('state', stateObj.code);
      } else {
        form.setValue('state', '');
      }
    } else {
      form.setValue('state', '');
    }
  }, [form, fetchStatesFromAPI]);

  // Set initial country on mount if provided
  useEffect(() => {
    if (initialCountry && !watchedValues.country) {
      form.setValue('country', initialCountry);
    }
  }, [initialCountry, form, watchedValues.country]);

  // Initialize states when country changes
  useEffect(() => {
    const initializeStates = async () => {
      const currentCountry = watchedValues.country || initialCountry || 'US';
      if (!currentCountry) return;
      
      const upperCountry = currentCountry.toUpperCase().slice(0, 2);
      
      let states = await fetchStatesFromAPI(upperCountry);
      if (!states.length) {
        states = getStatesForCountry(upperCountry);
      }
      setAvailableStates(states);

      // Get current state value from form
      const currentState = form.getValues('state');
      
      // If states are available, try to match and set the state
      if (states.length > 0) {
        // First, try to set from initialState prop if provided and no current state
        if (initialState && !currentState) {
          const stateObj = states.find(s =>
            s.name.toLowerCase() === initialState.toLowerCase() ||
            s.code.toLowerCase() === initialState.toLowerCase()
          );
          if (stateObj) {
            form.setValue('state', stateObj.code);
            hasInitializedStates.current = true;
            isInitialMount.current = false;
            return;
          }
        }
        
        // If there's a current state value, try to preserve it (especially on initial mount)
        if (currentState) {
          const stateObj = states.find(s =>
            s.name.toLowerCase() === currentState.toLowerCase() ||
            s.code.toLowerCase() === currentState.toLowerCase()
          );
          if (stateObj) {
            // Update to use the code format if it's a name match
            form.setValue('state', stateObj.code);
          }
          // If no match found, keep the current state value (might be valid for text input)
        } else if (isInitialMount.current && initialState) {
          // On initial mount, try to set from initialState prop
          const stateObj = states.find(s =>
            s.name.toLowerCase() === initialState.toLowerCase() ||
            s.code.toLowerCase() === initialState.toLowerCase()
          );
          if (stateObj) {
            form.setValue('state', stateObj.code);
          } else {
            form.setValue('state', initialState);
          }
        }
      } else {
        // No states available (text input mode), preserve current state or set initialState
        if (initialState && !currentState) {
          form.setValue('state', initialState);
        }
      }
      
      hasInitializedStates.current = true;
      isInitialMount.current = false;
    };

    initializeStates();
  }, [watchedValues.country, initialCountry, initialState, form, fetchStatesFromAPI]);

  return (
    <div id="billing-address-form" className="form-section">
      <h3>Billing Address</h3>
      <FormField
        control={form.control}
        name="address1"
        render={({ field }) => (
          <FormItem className="form-field">
            <FormLabel className="form-label">Street Address</FormLabel>
            <FormControl>
              <input
                {...field}
                data-ccbill="address1"
                type="text"
                placeholder="123 Main Street"
                className={`form-input ${shouldShowError('address1') ? 'error' : ''}`}
                disabled={isEditMode}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="form-grid-2">
        <FormField
          control={form.control}
          name="city"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">City</FormLabel>
              <FormControl>
                <input
                  {...field}
                  data-ccbill="city"
                  type="text"
                  placeholder="New York"
                  className={`form-input ${shouldShowError('city') ? 'error' : ''}`}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="state"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">State/Province</FormLabel>
              <FormControl>
                {availableStates.length > 0 ? (
                  <select
                    {...field}
                    data-ccbill="state"
                    className={`form-select ${shouldShowError('state') ? 'error' : ''}`}
                    style={{
                      backgroundImage: "url('data:image/svg+xml;charset=utf-8,<svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 20 20\"><path stroke=\"%239ca3af\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.5\" d=\"m6 8 4 4 4-4\"/></svg>')",
                      backgroundSize: '1.25em 1.25em',
                      backgroundPosition: 'calc(100% - 12px) center'
                    }}
                  >
                    <option value="">Select</option>
                    {availableStates.map(state => (
                      <option key={state.code} value={state.code}>
                        {state.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    {...field}
                    data-ccbill="state"
                    type="text"
                    placeholder="NY"
                    className={`form-input ${shouldShowError('state') ? 'error' : ''}`}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div className="form-grid-2">
        <FormField
          control={form.control}
          name="zipcode"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">ZIP/Postal Code</FormLabel>
              <FormControl>
                <input
                  {...field}
                  data-ccbill="postalCode"
                  type="text"
                  placeholder="10001"
                  className={`form-input ${shouldShowError('zipcode') ? 'error' : ''}`}
                  disabled={isEditMode}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="country"
          render={({ field }) => (
            <FormItem className="form-field">
              <FormLabel className="form-label">Country</FormLabel>
              <FormControl>
                <select
                  {...field}
                  data-ccbill="country"
                  onChange={(e) => {
                    field.onChange(e);
                    handleCountryChange(e.target.value);
                  }}
                  className={`form-select ${shouldShowError('country') ? 'error' : ''}`}
                  style={{
                    backgroundImage: "url('data:image/svg+xml;charset=utf-8,<svg xmlns=\"http://www.w3.org/2000/svg\" fill=\"none\" viewBox=\"0 0 20 20\"><path stroke=\"%239ca3af\" stroke-linecap=\"round\" stroke-linejoin=\"round\" stroke-width=\"1.5\" d=\"m6 8 4 4 4-4\"/></svg>')",
                    backgroundSize: '1.25em 1.25em',
                    backgroundPosition: 'calc(100% - 12px) center'
                  }}
                >
                  <option value="">Select</option>
                  {countries.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </FormControl>
              <FormMessage />
              {MASTERCARD_ONLY_COUNTRIES.includes(watchedValues.country) && (
                <div className="error-message">
                  Only MasterCard is accepted for this country.
                </div>
              )}
              {VISA_ONLY_COUNTRIES.includes(watchedValues.country) && (
                <div className="error-message">
                  Only Visa is accepted for this country.
                </div>
              )}
            </FormItem>
          )}
        />
      </div>
    </div>
  );
};

export default BillingAddressForm;

